import React, { useEffect, useState, useCallback } from 'react';
import { Plus, User, Edit, RefreshCw, Mail, Phone, Search, Filter, X } from 'lucide-react';
import { fetchTeamMembers, fetchRoles, fetchTeams, selectTeamMembers, selectTeamLoading, selectRoles, selectTeams } from '../../store/slices/teamSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import TeamMemberForm from './TeamMemberForm';
import PopupComponent from '../../components/PopupComponent';
import logger from '../../utils/logger';

const TeamMembersList: React.FC = () => {
  const dispatch = useAppDispatch();
  const members = useAppSelector(selectTeamMembers);
  const loading = useAppSelector(selectTeamLoading);
  const roles = useAppSelector(selectRoles);
  const teams = useAppSelector(selectTeams);
  const currentUser = useAppSelector((state) => state.whatsapp.user.currentUser);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(true); // Always show filters
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(100);

  // Fetch data with filters
  const fetchMembersWithFilters = () => {
    const params: any = {
      page: currentPage,
      perPage,
    };

    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    if (selectedTeams.length > 0) {
      params.teams = selectedTeams.join(','); // Send team IDs as comma-separated string
    }

    logger.log('API Call Params:', params); // Debug log to see what's being sent
    dispatch(fetchTeamMembers(params));
  };

  // Fetch roles and teams only once on mount
  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchTeams({ page: 1, perPage: 100 }));
  }, [dispatch]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchMembersWithFilters();
    }, searchTerm ? 500 : 0); // 500ms delay for search, immediate for other filters

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedTeams, currentPage, perPage]);

  const handleCreateMember = () => {
    setEditingMember(null);
    setShowForm(true);
  };

  const handleEditMember = (member: any) => {
    setEditingMember(member);
    setShowForm(true);
  };

  const handleRefresh = () => {
    fetchMembersWithFilters();
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingMember(null);
    fetchMembersWithFilters();
  };

  // Search handler with debounce
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Teams filter handler - use team ID instead of name
  const handleTeamFilter = (teamId: string) => {
    const newSelectedTeams = selectedTeams.includes(teamId)
      ? selectedTeams.filter(t => t !== teamId)
      : [...selectedTeams, teamId];

    setSelectedTeams(newSelectedTeams);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTeams([]);
    setCurrentPage(1);
  };


  const getStatusColor = (isActive: boolean) => {
    return isActive ? '#28a745' : '#dc3545';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  // Helper function to get role name from role ID
  const getRoleName = (roleId?: string) => {
    if (!roleId || !roles) return 'Employee';
    const role = roles.find(r => r._id === roleId);
    return role?.displayName || role?.name || 'Employee';
  };

  // Helper function to get team/organization info
  const getTeamInfo = (member: any) => {
    const hasTeams = member.teams && Array.isArray(member.teams) && member.teams.length > 0;
    const isOrgMember = member.isOrganizationMember;

    // Get organization name from currentUser or member data
    const orgName = currentUser?.organization?.name || member.organization?.name || 'Organization';

    // Case 1: Has teams AND is org employee - show both
    if (hasTeams && isOrgMember) {
      const teamCount = member.teams.length;
      return `${orgName} + ${teamCount} team${teamCount !== 1 ? 's' : ''}`;
    }

    // Case 2: Has teams but NOT org employee - show teams only
    if (hasTeams && !isOrgMember) {
      const teamCount = member.teams.length;
      return `${teamCount} team${teamCount !== 1 ? 's' : ''}`;
    }

    // Case 3: No teams but IS org employee - show organization only
    if (!hasTeams && isOrgMember) {
      return orgName;
    }

    // Case 4: No teams and NOT org employee - fallback
    return 'No teams assigned';
  };


  return (
    <div className="team-members-container">
      {/* Integrated Header with Search and Actions */}
      <div className="team-members-header-integrated">
        <div className="header-top-row">
          <h3 className="members-title">Team Members ({members?.length || 0})</h3>

          <div className="search-input-wrapper">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="header-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={loading?.members}
            >
              <RefreshCw size={14} className={loading?.members ? 'spinning' : ''} />
            </button>
            <button className="btn-primary" onClick={handleCreateMember}>
              <Plus size={14} />
              Add Member
            </button>
          </div>
        </div>

        {/* Teams Filter Bar */}
        <div className="header-filter-row">
          <div className="teams-filter-container">
            {teams?.filter(team => team.name && team._id).map((team) => (
              <button
                key={team._id}
                className={`filter-chip ${selectedTeams.includes(team._id!) ? 'active' : ''}`}
                onClick={() => handleTeamFilter(team._id!)}
              >
                {team.name}
                {selectedTeams.includes(team._id!) && <X size={12} />}
              </button>
            ))}
          </div>

          {(searchTerm || selectedTeams.length > 0) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {loading?.members ? (
        <div className="loading-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="member-card loading">
              <div className="loading-placeholder"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="members-grid">
          {members?.length === 0 ? (
            <div className="empty-state">
              <User size={64} />
              <h4>No team members found</h4>
              <p>Get started by adding your first team member to collaborate and manage tasks together</p>
              <button className="btn-primary" onClick={handleCreateMember}>
                <Plus size={16} />
                Add Your First Member
              </button>
            </div>
          ) : (
            members?.map((member) => (
              <div
                key={member._id}
                className="member-card"
                onClick={() => handleEditMember(member)}
              >
                <div className="member-card-header">
                  <div className="member-avatar">
                    <User size={24} />
                  </div>
                  <div className="member-card-content">
                    <div className="member-header-row">
                      <h4 className="member-name">
                        {member.firstName} {member.lastName || ''}
                      </h4>
                      <span className="member-role">{getRoleName(member.role)}</span>
                    </div>
                  </div>
                  <div
                    className="member-status"
                    style={{ backgroundColor: getStatusColor(member.isActive ?? true) }}
                  >
                    {getStatusText(member.isActive ?? true)}
                  </div>
                </div>

                <div className="member-card-content">
                  <p className="member-team-info">{getTeamInfo(member)}</p>

                  <div className="member-contact">
                    {member.email && (
                      <div className="contact-item">
                        <Mail size={14} />
                        <span>{member.email}</span>
                      </div>
                    )}
                    {member.phone && (
                      <div className="contact-item">
                        <Phone size={14} />
                        <span>{member.phone}</span>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Team Member Form Popup */}
      <PopupComponent
        isOpen={showForm}
        onClose={handleFormClose}
        title={editingMember ? 'Edit Team Member' : 'Add New Team Member'}
        size="lg"
      >
        <TeamMemberForm member={editingMember} onClose={handleFormClose} />
      </PopupComponent>
    </div>
  );
};

export default TeamMembersList;